"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PageChallengeSchema } from "@/sanity/pages/PageChallengeTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import checkbox from "@/public/challenge/checkbox.svg";
import { motion } from "framer-motion";
import { PageCompetitionAffiliateSchema } from "@/sanity/pages/PageCompetitionAffiliateTemplate";
import { CTAButton } from "@/shadcn/ui/button";
import { PageCompetitionAffiliateV2Schema } from "@/sanity/pages/PageCompetitionAffiliateV2Template";

export default function DealSection({
  data,
  className,
}: {
  data: PageCompetitionAffiliateV2Schema;
  className?: string;
}) {
  return (
    <section className={cn("relative overflow-clip", className)}>
      <div className="container flex flex-col items-start lg:items-center pt-[7rem] lg:pt-[12.25rem] pb-[7rem] lg:pb-[20rem] relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <ChipSerif text={data.dealChip} />
        </motion.div>

        {data?.dealTitle && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="richtext richtext-start lg:richtext-center sm:w-[45rem] lg:w-[min(55rem,100%)]"
          >
            <PortableText
              value={data.dealTitle}
              components={richTextComponent}
            />
          </motion.div>
        )}

        <div className="mt-[3rem] lg:mt-[4.75rem] w-[min(175rem,100%)] grid grid-cols-1 lg:grid-cols-3 gap-[2.75rem] lg:gap-[5.13rem]">
          {data.dealList &&
            data.dealList.length > 0 &&
            data.dealList.map((item, index) => (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{
                  duration: 0.3,
                  delay: 0.2 * index,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
                key={"deal" + index}
                className="rounded-[2rem] lg:rounded-[2.5rem] bg-[#FCFDFD] border border-[#F1F2F2] p-[3rem] lg:px-[4rem] lg:py-[5rem] flex flex-col gap-[2.75rem]"
              >
                <span className="hidden lg:block mb-[19rem] text-[#6DA991] text-[7rem] font-medium leading-none tabular-nums">
                  0{index + 1}
                </span>
                {item.image && (
                  <div className="p-[1rem] py-[1.15rem] lg:px-[1.25rem] bg-[#A3DEB266] lg:bg-[#F0F4F2] w-fit rounded-[1rem] font-geist">
                    <Image
                      src={item.image}
                      alt=""
                      className="!w-[3rem] lg:!w-[3.5rem] "
                    />
                  </div>
                )}
                <span className="prose-headings:text-[#103037] prose-headings:font-geist prose-headings:text-[3rem] prose-headings:font-medium prose-headings:tracking-[-0.09rem] prose-headings:leading-[120%] prose-headings:mb-[2rem] lg:prose-p:w-[90%] [text-wrap-style:balance]">
                  <PortableText
                    value={item.title}
                    components={richTextComponent}
                  />
                </span>
              </motion.div>
            ))}
        </div>

        {data.dealCTA && (
          <div className="w-full lg:w-fit mt-[4.25rem]">
            <CTAButton data={data.dealCTA} />
          </div>
        )}
      </div>
    </section>
  );
}
